// frontend/src/components/notifications/NotificationChannelMatrix.jsx
//
// Matriz de configuración: empresa × usuario × evento × canal.
//
// Contesta "¿quién tiene encendido qué?" de un vistazo. Es la pregunta previa a
// la auditoría de envíos: si aquí el canal está apagado, no hay envío que buscar.
//
// El estado de cada celda lo calcula el backend con la MISMA cadena de compuertas
// que aplica el envío real, así que lo que se ve aquí es lo que va a pasar. Se
// distinguen tres estados a propósito:
//   · encendido   — el usuario lo recibirá por ese canal.
//   · apagado     — decisión deliberada (evento, canal o reporte apagado).
//   · sin contacto— encendido pero SIN dato para entregarlo: nunca llegará.
// El tercero es el que se escapaba: en pantalla parecía activo y no llegaba nada.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, FormControl, InputLabel, MenuItem, OutlinedInput,
  Paper, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  Cancel as OffIcon,
  CheckCircle as OnIcon,
  Email as EmailIcon,
  NotificationsActive as PortalIcon,
  Phone as VoiceIcon,
  ReportProblem as NoContactIcon,
  Sms as SmsIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';

import { api } from '../../services/api';

const CHANNEL_META = {
  portal: { label: 'Portal', Icon: PortalIcon },
  email: { label: 'Correo', Icon: EmailIcon },
  sms: { label: 'SMS', Icon: SmsIcon },
  whatsapp: { label: 'WhatsApp', Icon: WhatsAppIcon },
  voice: { label: 'Llamada', Icon: VoiceIcon },
};

const STATE_META = {
  on: { Icon: OnIcon, color: 'success.main', label: 'Encendido' },
  off: { Icon: OffIcon, color: 'text.disabled', label: 'Apagado' },
  no_contact: { Icon: NoContactIcon, color: 'warning.main', label: 'Sin contacto' },
};

const channelLabel = (channel) => CHANNEL_META[channel]?.label || channel;

const Cell = ({ cell }) => {
  const meta = STATE_META[cell?.state] || STATE_META.off;
  const { Icon } = meta;
  return (
    <Tooltip title={cell?.reason || meta.label}>
      <Icon sx={{ fontSize: 18, color: meta.color, verticalAlign: 'middle' }} />
    </Tooltip>
  );
};

// `companyId`: 'all' para la vista Solo Arsenal. Sin valor, el backend responde
// con la empresa activa; el alcance lo decide el backend, no este componente.
const NotificationChannelMatrix = ({ companyId }) => {
  const [data, setData] = useState({ events: [], channels: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [visibleEvents, setVisibleEvents] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.notificationPreferences.channelMatrix(companyId ? { company_id: companyId } : {})
      .then((res) => {
        setData(res.data);
        setVisibleEvents((res.data.events || []).map((e) => e.key));
      })
      .catch((err) => setError(err?.response?.data?.detail || 'No se pudo cargar la matriz de configuración'))
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(load, [load]);

  const events = useMemo(
    () => (data.events || []).filter((e) => visibleEvents.includes(e.key)),
    [data.events, visibleEvents],
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data.rows || [];
    return (data.rows || []).filter((row) => (
      `${row.user_name} ${row.user_email} ${row.company_name}`.toLowerCase().includes(term)
    ));
  }, [data.rows, search]);

  // La columna Empresa solo aporta cuando hay más de una: dentro de una empresa
  // repetiría el mismo valor en todas las filas.
  const showCompany = useMemo(
    () => new Set((data.rows || []).map((r) => r.company_id)).size > 1,
    [data.rows],
  );

  const channels = data.channels || [];
  const fixedCols = showCompany ? 2 : 1;

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          size="small" label="Buscar usuario" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
          <InputLabel>Eventos visibles</InputLabel>
          <Select
            multiple value={visibleEvents} input={<OutlinedInput label="Eventos visibles" />}
            onChange={(e) => setVisibleEvents(e.target.value)}
            renderValue={(selected) => `${selected.length} de ${(data.events || []).length}`}
          >
            {(data.events || []).map((event) => (
              <MenuItem key={event.key} value={event.key}>{event.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {Object.entries(STATE_META).map(([key, meta]) => (
            <Stack key={key} direction="row" spacing={0.5} alignItems="center">
              <meta.Icon sx={{ fontSize: 16, color: meta.color }} />
              <Typography variant="caption" color="text.secondary">{meta.label}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              {showCompany && <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>Empresa</TableCell>}
              <TableCell rowSpan={2} sx={{ fontWeight: 700 }}>Usuario</TableCell>
              {events.map((event, index) => (
                <TableCell
                  key={event.key} colSpan={channels.length} align="center"
                  sx={{
                    fontWeight: 700, borderLeft: 1, borderColor: 'divider',
                    bgcolor: index % 2 ? 'transparent' : 'action.hover',
                  }}
                >
                  {event.label}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {events.map((event, index) => channels.map((channel, channelIndex) => {
                const { Icon } = CHANNEL_META[channel] || {};
                return (
                  <TableCell
                    key={`${event.key}-${channel}`} align="center"
                    sx={{
                      py: 0.5, borderLeft: channelIndex === 0 ? 1 : 0, borderColor: 'divider',
                      bgcolor: index % 2 ? 'transparent' : 'action.hover',
                    }}
                  >
                    <Tooltip title={channelLabel(channel)}>
                      {Icon ? <Icon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        : <span>{channel}</span>}
                    </Tooltip>
                  </TableCell>
                );
              }))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={fixedCols + events.length * channels.length} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Sin usuarios para mostrar</Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={`${row.company_id}-${row.user_id}`} hover>
                {showCompany && <TableCell>{row.company_name}</TableCell>}
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{row.user_name}</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary">{row.user_email}</Typography>
                    {!row.configured && (
                      <Tooltip title="Este usuario nunca abrió sus preferencias en esta empresa: se muestran los valores por defecto, que son los que usaría el envío.">
                        <Chip size="small" variant="outlined" label="Sin configurar" sx={{ height: 18, fontSize: 10 }} />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                {events.map((event, index) => channels.map((channel, channelIndex) => (
                  <TableCell
                    key={`${event.key}-${channel}`} align="center"
                    sx={{
                      py: 0.5, borderLeft: channelIndex === 0 ? 1 : 0, borderColor: 'divider',
                      bgcolor: index % 2 ? 'transparent' : 'action.hover',
                    }}
                  >
                    <Cell cell={row.events?.[event.key]?.[channel]} />
                  </TableCell>
                )))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
};

export default NotificationChannelMatrix;
